/**
 * Confirmation Block Renderer
 * Renders binary choice dialogs (Yes/No, Confirm/Cancel, etc.)
 */

import React from "react";
import { Confirmation } from "@strivelabs/ui-blocks";
import type { UIBlock, UIActionEvent } from "@/types/ui-blocks";

export interface ConfirmationBlockRendererProps {
  block: UIBlock;
  onUIAction: (event: UIActionEvent) => void;
  /** When true, the component is disabled */
  readOnly?: boolean;
}

export function ConfirmationBlockRenderer({
  block,
  onUIAction,
  readOnly = false
}: ConfirmationBlockRendererProps) {
  const [disabled, setDisabled] = React.useState(readOnly);
  if (block.type !== 'confirmation') return null;
  const { content } = block;

  // If a resolvedAction is stored (from history), highlight which button was chosen
  const resolvedAction = content.resolvedAction as { value: string; label: string } | undefined;

  const handleAction = (action: { label: string; value: string }) => {
    if (disabled) return;

    // Emit UI action event back to the chat system
    onUIAction({
      type: "ui_action",
      component: "confirmation",
      label: content.label,
      state: { action, confirmed: true }
    });

    setDisabled(true);
  };

  // When showing history with a resolved action, override variants to indicate choice
  const confirmAction = resolvedAction && resolvedAction.value === content.confirmAction?.value
    ? { ...content.confirmAction, variant: 'primary' as const }
    : resolvedAction
      ? { ...content.confirmAction, variant: 'outline' as const }
      : content.confirmAction;

  const cancelAction = content.cancelAction
    ? resolvedAction && resolvedAction.value === content.cancelAction?.value
      ? { ...content.cancelAction, variant: 'primary' as const }
      : resolvedAction
        ? { ...content.cancelAction, variant: 'outline' as const }
        : content.cancelAction
    : undefined;

  return (
    <Confirmation
      label={content.label}
      description={content.description}
      confirmAction={confirmAction}
      cancelAction={cancelAction}
      onAction={handleAction}
      disabled={disabled}
    />
  );
}
